import { findAll } from 'highlight-words-core';
import { useMemo } from 'react';

type HighlightType = 'inclusion' | 'exclusion' | 'both' | null;
type Highlight = {
  text: string;
  type: HighlightType;
};
type Chunk = {
  highlight: boolean;
  start: number;
  end: number;
};
type ExtractHighlightsResult = {
  highlights: Highlight[];
};

const determineType = (
  texts: string[],
  includeSet: Set<string>,
  excludeSet: Set<string>
): HighlightType => {
  const lowerTexts = texts.map((text) => text.toLowerCase());
  const inInclusion = lowerTexts.some((text) => includeSet.has(text));
  const inExclusion = lowerTexts.some((text) => excludeSet.has(text));

  if (inInclusion && inExclusion) return 'both';
  if (inInclusion) return 'inclusion';
  if (inExclusion) return 'exclusion';
  return null;
};

const isFullWordMatch = (start: number, end: number, text: string) => {
  const charBefore = start === 0 ? undefined : text[start - 1];
  const charAfter = end === text.length ? undefined : text[end];

  const isBoundary = (char?: string) => !char || /\W/.test(char);

  return isBoundary(charBefore) && isBoundary(charAfter);
};

const useOptimizedKeywords = (
  inclusionWords: readonly string[],
  exclusionWords: readonly string[]
) => {
  return useMemo(() => {
    const normalizeWords = (words: readonly string[]) =>
      words.map((w) => w.trim().toLowerCase()).filter((w) => w.length > 0);

    return {
      includeSet: new Set(normalizeWords(inclusionWords)),
      excludeSet: new Set(normalizeWords(exclusionWords)),
      searchWords: [
        ...normalizeWords(inclusionWords),
        ...normalizeWords(exclusionWords),
      ],
    };
  }, [inclusionWords, exclusionWords]);
};

const allHighlightedChunks = (text: string, searchWords: string[]): Chunk[] => {
  const allChunks = new Set<Chunk>();

  searchWords.forEach((word) => {
    findAll({
      searchWords: [word],
      textToHighlight: text,
      autoEscape: true,
    }).forEach((chunk) => {
      // Ignore non-highlights and matches that aren't full words:
      if (chunk.highlight && isFullWordMatch(chunk.start, chunk.end, text)) {
        allChunks.add(chunk);
      }
    });
  });

  return Array.from(allChunks);
};

const overlappingChunks = (allChunks: Chunk[], index: number): Chunk[] => {
  return allChunks.filter(
    (chunk: Chunk) => chunk.start <= index && chunk.end > index
  );
};

const matchingChunks = (a: Chunk[], b: Chunk[]): boolean => {
  return (
    a.length == b.length &&
    a.every((object: Chunk, index: number) => object === b[index])
  );
};

// This function takes a given string of text, and returns the text split up
// into highlights - each substring paired with the type of highlight:
// inclusion, exclusion, both, or neither (null).
//
// The four arguments are:
//  - text - the full string to be matching against.
//  - inclusionWords - the phrases to match against as inclusions
//  - exclusionWords - the phrases to match against as exclusions
//  - isEnabled - indicating whether highlighting should happen at all.
//
// If isEnabled is set to false, or if there's no inclusion/exclusion words, or
// also if there's no matches of those words, then the input text is returned in
// full, with a highlight type of null.
export const useExtractHighlights = ({
  text,
  inclusionWords,
  exclusionWords,
  isEnabled,
}: {
  text: string;
  inclusionWords: readonly string[];
  exclusionWords: readonly string[];
  isEnabled: boolean;
}): ExtractHighlightsResult => {
  const sanitisedText = text.replace(/\s+/g, ' ').trim();

  const noHighlights = useMemo(
    () => ({ highlights: [{ text: sanitisedText, type: null }] }),
    [sanitisedText]
  );
  const { includeSet, excludeSet, searchWords } = useOptimizedKeywords(
    inclusionWords,
    exclusionWords
  );

  const createHighlight = (
    start: number,
    end: number,
    chunks: Chunk[]
  ): Highlight => {
    const highlightText = sanitisedText.slice(start, end);
    if (chunks.length === 0) {
      return { text: highlightText, type: null };
    }

    const chunkTexts: string[] = chunks.map((chunk: Chunk): string =>
      sanitisedText.slice(chunk.start, chunk.end)
    );
    const highlightType = determineType(chunkTexts, includeSet, excludeSet);

    return { text: highlightText, type: highlightType };
  };

  const result = useMemo(() => {
    if (!isEnabled || searchWords.length === 0) return noHighlights;

    // Preparing the collection for our highlights:
    const highlights: Highlight[] = [];

    // A call to the highlight-words-core package gives us the entire string
    // back, split up into chunks. All chunks have the following attributes:
    //  - start (index where the chunk starts)
    //  - end (index where the chunk stops)
    //  - highlight (boolean, does this chunk match the search term/s)
    //
    // We're ignoring chunks that are not matches, only returning chunks here
    // where `highlight` is true. We will be making our way through the full
    // string, splitting it by each point where the matching context changes,
    // to build an array of highlights that covers the full string.

    // This gives us all matches for all search terms, some of which may
    // overlap:
    const allChunks: Chunk[] = allHighlightedChunks(sanitisedText, searchWords);

    // If it turns out there's no matches, let's quickly exit:
    if (allChunks.length === 0) return noHighlights;

    // Otherwise, let's build our set of change points - the inflections where
    // we know a highlight has started or finished. We want this to be a unique
    // collection, and sorted - so we can progress through the string in order!
    const changePoints = new Set<number>(
      [
        ...allChunks.map((chunk) => chunk.start),
        ...allChunks.map((chunk) => chunk.end),
      ].sort((a, b) => a - b)
    );

    let highlightStart = 0;
    // At the beginning of the string, let's get our initial state: any chunk
    // that covers this first character in the string.
    let currentChunks = overlappingChunks(allChunks, 0);

    changePoints.forEach((i) => {
      // Then, at each further change point, get the new set of relevant chunks:
      const nextChunks = overlappingChunks(allChunks, i);

      // If they're different, we have ourselves a state change!
      if (!matchingChunks(currentChunks, nextChunks)) {
        // So, save a new highlight string, with the appropriate type
        // (none/null, inclusion, exclusion, both)
        highlights.push(createHighlight(highlightStart, i, currentChunks));

        // And then set things up for the next highlight:
        highlightStart = i;
        currentChunks = nextChunks;
      }
    });

    // Finalise the last highlight if the end of the string wasn't matched:
    if (highlightStart < sanitisedText.length) {
      highlights.push(
        createHighlight(highlightStart, sanitisedText.length, currentChunks)
      );
    }

    return { highlights };
  }, [text, inclusionWords, exclusionWords, isEnabled, noHighlights]);

  return result;
};
